(() => {
  const READY_ATTRIBUTE = 'data-ips-product-carousel-ready';
  const INTERVAL = 5000;
  const SCROLL_SETTLE_DELAY = 140;

  class IPSProductCarousel {
    constructor(gallery) {
      if (!gallery || gallery.hasAttribute(READY_ATTRIBUTE)) return;

      this.gallery = gallery;
      this.viewer = gallery.querySelector('[id^="Slider-Gallery-"]');
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion = this.motionPreference.matches;
      this.timer = null;
      this.scrollTimer = null;
      this.loopResetTimer = null;
      this.pendingLoopReset = false;
      this.hoverPaused = false;
      this.focusPaused = false;

      const buttons = Array.from(
        gallery.querySelectorAll(
          '.thumbnail-list__item:not(.thumbnail-list_item--variant) button.thumbnail'
        )
      );

      if (!this.viewer || buttons.length < 2) return;

      const viewerItems = Array.from(this.viewer.children);
      this.entries = buttons
        .map((button) => {
          const thumbnailItem = button.closest('[data-target]');
          const mediaId = thumbnailItem?.dataset.target;
          const slide = viewerItems.find((item) => item.dataset.mediaId === mediaId);
          return slide ? { button, thumbnailItem, slide } : null;
        })
        .filter(Boolean);

      if (this.entries.length < 2) return;

      this.thumbnails = this.entries.map((entry) => entry.button);
      this.slides = this.entries.map((entry) => entry.slide);
      this.index = Math.max(
        0,
        this.thumbnails.findIndex((button) => button.getAttribute('aria-current') === 'true')
      );

      this.loopClone = this.createLoopClone(this.slides[0]);
      this.viewer.appendChild(this.loopClone);
      this.gallery.setAttribute(READY_ATTRIBUTE, 'true');

      this.bindEvents();
      this.activate(this.index, false);
      this.jumpTo(this.slides[this.index]);
      this.schedule();
    }

    createLoopClone(source) {
      const clone = source.cloneNode(true);
      clone.classList.remove('is-active', 'slider__slide', 'scroll-trigger', 'animate--fade-in');
      clone.classList.add('ips-product-carousel__clone');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-media-id');
      clone.removeAttribute('id');

      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone
        .querySelectorAll('[data-media-id]')
        .forEach((element) => element.removeAttribute('data-media-id'));
      clone
        .querySelectorAll('button, a, input, select, textarea, [tabindex]')
        .forEach((element) => {
          element.setAttribute('tabindex', '-1');
          if ('disabled' in element) element.disabled = true;
        });

      return clone;
    }

    bindEvents() {
      this.gallery.addEventListener(
        'click',
        (event) => {
          const target = event.target instanceof Element ? event.target : null;
          const button = target?.closest('button.thumbnail');
          const nextIndex = button ? this.thumbnails.indexOf(button) : -1;
          if (nextIndex < 0) return;

          event.preventDefault();
          event.stopImmediatePropagation();
          button.focus({ preventScroll: true });
          this.goTo(nextIndex, { animate: true, announce: true });
        },
        true
      );

      this.viewer.addEventListener(
        'scroll',
        () => {
          window.clearTimeout(this.scrollTimer);
          this.scrollTimer = window.setTimeout(
            () => this.syncFromScroll(),
            SCROLL_SETTLE_DELAY
          );
        },
        { passive: true }
      );

      this.gallery.addEventListener('mouseenter', () => {
        this.hoverPaused = true;
        this.pause();
      });

      this.gallery.addEventListener('mouseleave', () => {
        this.hoverPaused = false;
        this.schedule();
      });

      this.gallery.addEventListener('focusin', () => {
        this.focusPaused = true;
        this.pause();
      });

      this.gallery.addEventListener('focusout', (event) => {
        if (event.relatedTarget && this.gallery.contains(event.relatedTarget)) return;
        this.focusPaused = false;
        this.schedule();
      });

      document.addEventListener('visibilitychange', () => this.schedule());

      window.addEventListener(
        'resize',
        () => {
          window.requestAnimationFrame(() => this.jumpTo(this.slides[this.index]));
        },
        { passive: true }
      );

      const handleMotionChange = (event) => {
        this.reducedMotion = event.matches;
        this.jumpTo(this.slides[this.index]);
        this.schedule();
      };

      if (typeof this.motionPreference.addEventListener === 'function') {
        this.motionPreference.addEventListener('change', handleMotionChange);
      } else {
        this.motionPreference.addListener(handleMotionChange);
      }
    }

    slidePosition(slide) {
      const viewerRect = this.viewer.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      return this.viewer.scrollLeft + slideRect.left - viewerRect.left;
    }

    scrollTo(slide, animate) {
      if (!slide) return;

      if (!animate || this.reducedMotion) {
        this.jumpTo(slide);
        return;
      }

      this.viewer.scrollTo({
        left: this.slidePosition(slide),
        behavior: 'smooth'
      });
    }

    jumpTo(slide) {
      if (!slide) return;

      this.viewer.classList.add('ips-product-carousel--jumping');
      this.viewer.scrollTo({
        left: this.slidePosition(slide),
        behavior: 'auto'
      });

      window.requestAnimationFrame(() => {
        this.viewer.classList.remove('ips-product-carousel--jumping');
      });
    }

    activate(nextIndex, announce) {
      const entry = this.entries[nextIndex];
      if (!entry) return;

      const scrollX = window.scrollX;
      const scrollY = window.scrollY;

      this.slides.forEach((slide) => slide.classList.remove('is-active'));
      entry.slide.classList.add('is-active');
      this.thumbnails.forEach((button) => button.removeAttribute('aria-current'));
      entry.button.setAttribute('aria-current', 'true');

      if (typeof this.gallery.playActiveMedia === 'function') {
        this.gallery.playActiveMedia(entry.slide);
      }

      if (typeof this.gallery.setActiveThumbnail === 'function') {
        this.gallery.setActiveThumbnail(entry.thumbnailItem);
      }

      if (announce && typeof this.gallery.announceLiveRegion === 'function') {
        this.gallery.announceLiveRegion(
          entry.slide,
          entry.thumbnailItem.dataset.mediaPosition
        );
      }

      if (Math.abs(window.scrollX - scrollX) > 1 || Math.abs(window.scrollY - scrollY) > 1) {
        window.scrollTo(scrollX, scrollY);
      }

      window.requestAnimationFrame(() => {
        if (Math.abs(window.scrollX - scrollX) > 1 || Math.abs(window.scrollY - scrollY) > 1) {
          window.scrollTo(scrollX, scrollY);
        }
      });
    }

    goTo(requestedIndex, options = {}) {
      const count = this.slides.length;
      const forwardLoop = options.forwardLoop && requestedIndex >= count;
      const nextIndex = ((requestedIndex % count) + count) % count;

      this.index = nextIndex;
      this.activate(nextIndex, Boolean(options.announce));

      if (forwardLoop && this.loopClone && !this.reducedMotion) {
        this.pendingLoopReset = true;
        this.scrollTo(this.loopClone, true);
        window.clearTimeout(this.loopResetTimer);
        this.loopResetTimer = window.setTimeout(() => this.resetLoop(), 1200);
      } else {
        this.pendingLoopReset = false;
        this.scrollTo(this.slides[nextIndex], options.animate !== false);
      }

      this.schedule();
    }

    syncFromScroll() {
      if (this.pendingLoopReset) {
        const distanceToClone = Math.abs(
          this.viewer.scrollLeft - this.slidePosition(this.loopClone)
        );
        if (distanceToClone <= 3) this.resetLoop();
        return;
      }

      const visualSlides = [...this.slides, this.loopClone];
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      visualSlides.forEach((slide, index) => {
        const distance = Math.abs(this.viewer.scrollLeft - this.slidePosition(slide));
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      if (nearestIndex === this.slides.length) {
        this.index = 0;
        this.activate(0, true);
        this.resetLoop();
        return;
      }

      if (nearestIndex !== this.index) {
        this.index = nearestIndex;
        this.activate(nearestIndex, true);
      }

      this.schedule();
    }

    resetLoop() {
      if (!this.pendingLoopReset && this.index !== 0) return;

      window.clearTimeout(this.loopResetTimer);
      this.pendingLoopReset = false;
      this.index = 0;
      this.activate(0, false);
      this.jumpTo(this.slides[0]);
      this.schedule();
    }

    pause() {
      window.clearTimeout(this.timer);
    }

    schedule() {
      window.clearTimeout(this.timer);

      if (
        this.reducedMotion ||
        document.hidden ||
        this.hoverPaused ||
        this.focusPaused
      ) {
        return;
      }

      this.timer = window.setTimeout(() => {
        this.goTo(this.index + 1, {
          animate: true,
          announce: false,
          forwardLoop: true
        });
      }, INTERVAL);
    }
  }

  // Product media motion is owned by assets/media-gallery.js.
  // Keep this legacy controller inactive to prevent competing timers and scroll handlers.
  const init = () => {};

  const initialize = () => init(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();

(() => {
  const READY_CLASS = 'ips-routine-ready';

  const arrowIcon = (direction) =>
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="' +
    (direction === 'previous' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6') +
    '" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  const enhance = (recommendations) => {
    if (!recommendations || recommendations.classList.contains(READY_CLASS)) return;

    const track = recommendations.querySelector('ul.product-grid');
    const heading = recommendations.querySelector('.related-products__heading');
    if (!track || !heading || track.children.length < 2) return;

    recommendations.classList.add(READY_CLASS);
    track.classList.add('ips-routine-track');

    const headingRow = document.createElement('div');
    const controls = document.createElement('div');
    const previous = document.createElement('button');
    const next = document.createElement('button');

    headingRow.className = 'ips-routine-heading-row';
    controls.className = 'ips-routine-controls';
    previous.type = 'button';
    previous.className = 'ips-routine-arrow ips-routine-arrow--previous';
    previous.setAttribute('aria-label', 'Show previous products');
    previous.innerHTML = arrowIcon('previous');
    next.type = 'button';
    next.className = 'ips-routine-arrow ips-routine-arrow--next';
    next.setAttribute('aria-label', 'Show more products');
    next.innerHTML = arrowIcon('next');

    heading.before(headingRow);
    headingRow.append(heading, controls);
    controls.append(previous, next);

    const updateControls = () => {
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      previous.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= maxScroll - 2;
      controls.hidden = maxScroll <= 2;
    };

    const move = (direction) => {
      const firstCard = track.querySelector('.grid__item');
      const styles = window.getComputedStyle(track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      const cardWidth = firstCard?.getBoundingClientRect().width || track.clientWidth * 0.8;
      const cardsPerMove = window.matchMedia('(max-width: 749px)').matches ? 1 : 2;

      track.scrollBy({
        left: direction * (cardWidth + gap) * cardsPerMove,
        behavior: 'smooth'
      });
    };

    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    track.addEventListener('scroll', () => requestAnimationFrame(updateControls), {
      passive: true
    });
    window.addEventListener('resize', updateControls, { passive: true });
    requestAnimationFrame(updateControls);
  };

  const scan = (root = document) => {
    root.querySelectorAll?.('product-recommendations.related-products').forEach(
      (recommendations) => {
        enhance(recommendations);

        if (
          !recommendations.classList.contains(READY_CLASS) &&
          !recommendations.dataset.ipsRoutineObserved
        ) {
          recommendations.dataset.ipsRoutineObserved = 'true';
          new MutationObserver(() => enhance(recommendations)).observe(recommendations, {
            childList: true,
            subtree: true
          });
        }
      }
    );
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();


// Product-page sticky purchase controls. Arqum 2026-09-14.
(() => {
  if (customElements.get('ips-sticky-cart')) return;

  class IPSStickyCart extends HTMLElement {
    connectedCallback() {
      this.source = document.getElementById(this.dataset.productInfo);
      if (!this.source) return;
      this.abort = new AbortController();
      this.inert = true;
      this.setAttribute('aria-hidden', 'true');
      this.innerHTML = `
        <div class="ips-sticky-cart__inner">
          <a class="ips-sticky-cart__product">
            <img class="ips-sticky-cart__image" alt="" width="52" height="52" loading="lazy" decoding="async" fetchpriority="low">
            <span class="ips-sticky-cart__copy">
              <strong class="ips-sticky-cart__title"></strong>
              <span class="ips-sticky-cart__variant"></span>
            </span>
          </a>
          <div class="ips-sticky-cart__price"></div>
          <div class="ips-sticky-cart__quantity">
            <button type="button" data-step="minus">−</button>
            <input type="number" inputmode="numeric" autocomplete="off">
            <button type="button" data-step="plus">+</button>
          </div>
          <button type="button" class="ips-sticky-cart__submit">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 7h14l1 14H4L5 7Zm4 1V6a3 3 0 0 1 6 0v2" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
            <span></span>
            <span class="ips-sticky-cart__spinner" aria-hidden="true"></span>
          </button>
          <p class="ips-sticky-cart__error" role="alert" hidden></p>
        </div>`;
      this.button = this.querySelector('.ips-sticky-cart__submit');
      this.buttonText = this.button.querySelector('span');
      this.quantity = this.querySelector('input');
      this.price = this.querySelector('.ips-sticky-cart__price');
      this.error = this.querySelector('.ips-sticky-cart__error');
      this.productLink = this.querySelector('a');
      this.productLink.href = '#' + this.source.id;
      this.queue = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => {
          this.frame = null;
          if (this.needsSync) this.sync();
          else this.updateVisibility();
        });
      };
      this.schedule = () => {
        this.needsSync = true;
        this.queue();
      };
      this.scheduleVisibility = () => this.queue();
      const events = { signal: this.abort.signal };
      this.source.addEventListener('change', this.schedule, events);
      this.source.addEventListener('input', this.schedule, events);
      window.addEventListener('scroll', this.scheduleVisibility, { ...events, passive: true });
      window.addEventListener('resize', this.scheduleVisibility, { ...events, passive: true });
      window.visualViewport?.addEventListener('resize', this.scheduleVisibility, events);
      document.addEventListener('toggle', this.scheduleVisibility, { ...events, capture: true });
      document.addEventListener('focusin', this.scheduleVisibility, events);
      document.addEventListener('focusout', this.scheduleVisibility, events);
      this.quantity.addEventListener('change', () => this.commitQuantity(), events);
      this.productLink.addEventListener('click', (event) => {
        event.preventDefault();
        const target = this.source.querySelector('variant-selects') || this.source;
        target.scrollIntoView({ behavior: this.motion(), block: 'center' });
        target.querySelector('select, input, button')?.focus({ preventScroll: true });
      }, events);
      this.querySelectorAll('[data-step]').forEach((control) => {
        control.addEventListener('click', () => {
          if (!this.commitQuantity()) return;
          const native = this.nativeQuantity()?.closest('quantity-input');
          native?.querySelector(`[name="${control.dataset.step}"]`)?.click();
          this.sync();
        }, events);
      });
      this.button.addEventListener('click', () => {
        const submit = this.nativeSubmit();
        if (!submit || submit.disabled || submit.getAttribute('aria-disabled') === 'true') return;
        if (!this.commitQuantity()) return;
        const form = submit.form;
        if (!form) return;
        if (!form.checkValidity()) {
          form.querySelector(':invalid')?.scrollIntoView({ behavior: this.motion(), block: 'center' });
          form.reportValidity();
          return;
        }
        form.requestSubmit(submit);
        this.sync();
      }, events);
      this.observer = new MutationObserver(this.schedule);
      this.observer.observe(this.source.querySelector('.product__info-container') || this.source, {
        childList: true, subtree: true, characterData: true, attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class', 'hidden', 'value', 'min', 'max', 'step']
      });
      this.modalObserver = new MutationObserver(this.scheduleVisibility);
      this.modalObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      document.querySelectorAll('cart-drawer, cart-notification, quick-add-modal, product-modal, dialog').forEach((modal) => {
        this.modalObserver.observe(modal, { attributes: true, attributeFilter: ['class', 'open'] });
      });
      this.offsetDirty = true;
      this.resizeObserver = new ResizeObserver(() => {
        this.offsetDirty = true;
        this.scheduleVisibility();
      });
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.source);
      this.sync();
    }

    nativeSubmit() {
      return this.source.querySelector('product-form button[name="add"]');
    }

    nativeQuantity() {
      return this.source.querySelector('input[name="quantity"]');
    }

    motion() {
      return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    commitQuantity() {
      const native = this.nativeQuantity();
      if (!native || this.quantity.disabled) return true;
      if (!this.quantity.reportValidity()) return false;
      if (!this.quantity.value) {
        this.quantity.value = native.value;
        return true;
      }
      if (native.value !== this.quantity.value) {
        native.value = this.quantity.value;
        native.dispatchEvent(new Event('input', { bubbles: true }));
        native.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return true;
    }

    sync() {
      this.needsSync = false;
      if (!this.source.isConnected) {
        this.remove();
        return;
      }
      const submit = this.nativeSubmit();
      if (!submit) {
        this.setVisible(false);
        return;
      }
      this.submit = submit;
      const title = this.source.querySelector('.product__title h1')?.textContent.trim() || '';
      this.querySelector('.ips-sticky-cart__title').textContent = title;
      const label = submit.querySelector('span')?.textContent.trim() || window.variantStrings?.addToCart || '';
      this.setAttribute('role', 'region');
      this.setAttribute('aria-label', `${title} — ${label}`);
      this.buttonText.textContent = label;
      this.button.disabled = submit.disabled || submit.getAttribute('aria-disabled') === 'true';
      const busy = submit.classList.contains('loading');
      this.button.setAttribute('aria-busy', String(busy));
      this.button.setAttribute('aria-label', `${label} — ${title}`);
      this.button.toggleAttribute('aria-haspopup', submit.hasAttribute('aria-haspopup'));
      if (submit.hasAttribute('aria-haspopup')) this.button.setAttribute('aria-haspopup', submit.getAttribute('aria-haspopup'));

      const nativePrice = this.source.querySelector('[id^="price-"] .price');
      const priceHtml = nativePrice?.outerHTML || '';
      if (this.priceSnapshot !== priceHtml) {
        this.priceSnapshot = priceHtml;
        const copy = nativePrice?.cloneNode(true);
        if (copy) {
          copy.removeAttribute('id');
          copy.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
          this.price.replaceChildren(copy);
        } else this.price.replaceChildren();
      }
      this.price.hidden = !nativePrice || Boolean(nativePrice.closest('.hidden'));
      let variant = null;
      try {
        variant = JSON.parse(this.source.querySelector('[data-selected-variant]')?.textContent || 'null');
      } catch {}
      const variantLabel = this.querySelector('.ips-sticky-cart__variant');
      variantLabel.textContent = variant?.title === 'Default Title' ? '' : variant?.title || '';
      variantLabel.hidden = !variantLabel.textContent;
      const image = this.querySelector('.ips-sticky-cart__image');
      const originalImage = this.source.querySelector('.product__media img');
      let imageUrl = variant?.featured_image?.src || originalImage?.currentSrc || originalImage?.src;
      if (imageUrl) {
        try {
          const thumbnail = new URL(imageUrl, document.baseURI);
          if (thumbnail.hostname === 'cdn.shopify.com' || thumbnail.pathname.includes('/cdn/shop/')) {
            thumbnail.searchParams.set('width', '160');
            imageUrl = thumbnail.href;
          }
        } catch {}
      }
      if (imageUrl && image.getAttribute('src') !== imageUrl) image.src = imageUrl;
      image.hidden = !imageUrl;

      const nativeQuantity = this.nativeQuantity();
      this.querySelector('.ips-sticky-cart__quantity').hidden = !nativeQuantity;
      if (nativeQuantity) {
        for (const attribute of ['min', 'max', 'step']) {
          const value = nativeQuantity.getAttribute(attribute);
          if (value === null) this.quantity.removeAttribute(attribute);
          else this.quantity.setAttribute(attribute, value);
        }
        this.quantity.required = true;
        this.quantity.disabled = nativeQuantity.disabled || busy;
        if (document.activeElement !== this.quantity) this.quantity.value = nativeQuantity.value;
        this.quantity.setAttribute('aria-label', nativeQuantity.getAttribute('aria-label') || nativeQuantity.labels?.[0]?.textContent.trim() || title);
        this.querySelectorAll('[data-step]').forEach((control) => {
          const native = nativeQuantity.closest('quantity-input')?.querySelector(`[name="${control.dataset.step}"]`);
          control.disabled = !native || native.disabled || native.classList.contains('disabled') || busy;
          control.setAttribute('aria-label', native?.getAttribute('aria-label') || native?.textContent.trim() || control.textContent);
        });
      }
      const errorWrapper = this.source.querySelector('.product-form__error-message-wrapper');
      const errorText = errorWrapper && !errorWrapper.hidden ? errorWrapper.querySelector('.product-form__error-message')?.textContent.trim() : '';
      this.error.hidden = !errorText;
      if (this.error.textContent !== (errorText || '')) this.error.textContent = errorText || '';
      this.updateVisibility();
    }

    updateVisibility() {
      if (!this.source.isConnected) {
        this.remove();
        return;
      }
      const submit = this.submit;
      if (!submit?.isConnected) {
        this.setVisible(false);
        this.schedule();
        return;
      }
      const covered = document.querySelector('cart-drawer.active, cart-notification.active, quick-add-modal[open], product-modal[open], dialog[open], details[open] .menu-drawer');
      const typingElsewhere = document.activeElement?.matches('textarea, input:not([type]), input[type="text"], input[type="email"], input[type="tel"], input[type="search"], input[type="url"], input[type="password"], input[type="number"]') && !this.contains(document.activeElement);
      this.setVisible(submit.getBoundingClientRect().bottom <= 0 && !covered && !typingElsewhere);
    }

    setVisible(visible) {
      const changed = this.classList.contains('is-visible') !== visible;
      if (changed) {
        this.classList.toggle('is-visible', visible);
        this.inert = !visible;
        this.setAttribute('aria-hidden', String(!visible));
      }
      if (document.documentElement.classList.contains('ips-sticky-cart-visible') !== visible) {
        document.documentElement.classList.toggle('ips-sticky-cart-visible', visible);
      }
      if (!changed && !this.offsetDirty) return;
      this.offsetDirty = false;
      const offset = visible ? `${Math.ceil(this.getBoundingClientRect().height) + 28}px` : '0px';
      if (document.documentElement.style.getPropertyValue('--ips-sticky-cart-offset') !== offset) {
        document.documentElement.style.setProperty('--ips-sticky-cart-offset', offset);
      }
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.modalObserver?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.setVisible(false);
    }
  }

  customElements.define('ips-sticky-cart', IPSStickyCart);
  const mount = () => {
    const source = document.querySelector('product-info[id^="MainProduct-"]');
    const current = document.querySelector('ips-sticky-cart');
    if (current?.source === source) return;
    current?.remove();
    if (!source?.querySelector('product-form button[name="add"]')) return;
    const bar = document.createElement('ips-sticky-cart');
    bar.dataset.productInfo = source.id;
    document.body.append(bar);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
  document.addEventListener('product-info:loaded', () => requestAnimationFrame(mount));
  document.addEventListener('shopify:section:load', mount);
  document.addEventListener('shopify:section:unload', (event) => {
    const bar = document.querySelector('ips-sticky-cart');
    if (bar && event.target.contains(bar.source)) bar.remove();
  });
})();
